<template>
  <div :class="containerClass" class="custom-pagination-wrapper">
    <!-- Previous Button -->
    <button
      v-if="showPrevNext"
      type="button"
      :class="[prevClass, prevLinkClass, { [disabledClass]: currentPage <= 1 }]"
      :disabled="currentPage <= 1"
      @click="goToPage(currentPage - 1)"
      v-html="prevText"
    ></button>

    <!-- First Page -->
    <button
      v-if="showFirstLast && (shouldShowFirstPageOnMobile || shouldShowFirstPageOnDesktop)"
      type="button"
      :class="[
        pageClass,
        pageLinkClass,
        { [activeClass]: currentPage === 1 },
      ]"
      @click="goToPage(1)"
    >
      1
    </button>

    <!-- First Ellipsis -->
    <span
      v-if="showFirstLast && (shouldShowFirstEllipsisOnMobile || shouldShowFirstEllipsisOnDesktop)"
      :class="[breakViewClass, breakViewLinkClass]"
      v-html="breakViewText"
    ></span>

    <!-- Page Numbers -->
    <button
      v-for="page in visiblePages"
      :key="page"
      type="button"
      :class="[
        pageClass,
        pageLinkClass,
        { [activeClass]: page === currentPage },
      ]"
      @click="goToPage(page)"
    >
      {{ page }}
    </button>

    <!-- Last Ellipsis -->
    <span
      v-if="showFirstLast && shouldShowLastEllipsisOnDesktop"
      :class="[breakViewClass, breakViewLinkClass]"
      v-html="breakViewText"
    ></span>

    <!-- Last Page -->
    <button
      v-if="showFirstLast && shouldShowLastPageOnDesktop"
      type="button"
      :class="[
        pageClass,
        pageLinkClass,
        { [activeClass]: currentPage === totalPages },
      ]"
      @click="goToPage(totalPages)"
    >
      {{ totalPages }}
    </button>

    <!-- Next Button -->
    <button
      v-if="showPrevNext"
      type="button"
      :class="[
        nextClass,
        nextLinkClass,
        { [disabledClass]: currentPage >= totalPages },
      ]"
      :disabled="currentPage >= totalPages"
      @click="goToPage(currentPage + 1)"
      v-html="nextText"
    ></button>
  </div>
</template>

<script>
export default {
  name: 'CustomPagination',
  props: {
    modelValue: {
      type: Number,
      default: 1,
    },
    pageCount: {
      type: Number,
      required: true,
    },
    pageRange: {
      type: Number,
      default: 3,
    },
    marginPages: {
      type: Number,
      default: 1,
    },
    clickHandler: {
      type: Function,
      default: null,
    },
    // CSS Classes
    containerClass: {
      type: String,
      default: 'pagination',
    },
    pageClass: {
      type: String,
      default: 'page-item',
    },
    pageLinkClass: {
      type: String,
      default: 'page-link',
    },
    prevClass: {
      type: String,
      default: 'page-item prev',
    },
    prevLinkClass: {
      type: String,
      default: 'page-link prev-link',
    },
    nextClass: {
      type: String,
      default: 'page-item next',
    },
    nextLinkClass: {
      type: String,
      default: 'page-link next-link',
    },
    breakViewClass: {
      type: String,
      default: 'page-item break-view',
    },
    breakViewLinkClass: {
      type: String,
      default: 'page-link break-view-link',
    },
    activeClass: {
      type: String,
      default: 'active',
    },
    disabledClass: {
      type: String,
      default: 'disabled',
    },
    // Text
    prevText: {
      type: String,
      default: 'Previous',
    },
    nextText: {
      type: String,
      default: 'Next',
    },
    breakViewText: {
      type: String,
      default: '...',
    },
    // Options
    showPrevNext: {
      type: Boolean,
      default: true,
    },
    showFirstLast: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue'],
  data() {
    // Normalize initial value - handle both number and string from URL params
    // Don't default to 1 if prop is null - wait for watcher to set it
    const initialPage = this.modelValue != null ? Number(this.modelValue) : null;
    const validInitialPage = (initialPage && !isNaN(initialPage) && initialPage > 0) ? initialPage : null;
    return {
      // Local state to track active page for immediate UI updates
      // Start with prop value if valid, otherwise default to 1 (will be updated by watcher if prop loads)
      localPage: validInitialPage || 1,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      isUserAction: false, // Flag to prevent watcher from overwriting user-initiated changes
    };
  },
  mounted() {


    
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize);
      this.handleResize();
    }
    // Sync localPage with prop after mount (handles initial URL params)
    // Use nextTick to ensure parent has finished updating props
    this.$nextTick(() => {
      if (this.modelValue != null && this.modelValue !== '') {
        const numValue = Number(this.modelValue);
        if (!isNaN(numValue) && numValue > 0 && numValue !== this.localPage) {
          this.localPage = numValue;
        }
      }
    });
  },
  updated() {
    // Safety check: sync if prop changed after update (handles async prop updates)
    // But don't overwrite if user just clicked
    if (!this.isUserAction && this.modelValue != null && this.modelValue !== '') {
      const numValue = Number(this.modelValue);
      if (!isNaN(numValue) && numValue > 0 && numValue !== this.localPage) {
        this.localPage = numValue;
      }
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }
  },
  watch: {
    // Sync local state when prop changes (e.g., after async parent update or initial URL param)
    modelValue: {
      immediate: true,
      handler(newValue) {
        // Don't overwrite if user just clicked (give parent time to update)
        if (this.isUserAction) {
          return;
        }
        
        // Normalize value - handle both number and string from URL params
        if (newValue != null && newValue !== '') {
          const numValue = Number(newValue);
          if (!isNaN(numValue) && numValue > 0) {
            // Always update if we have a valid value that's different from current
            if (numValue !== this.localPage) {
              this.localPage = numValue;
            }
          }
        }
        // If newValue is null/undefined/empty, keep current value (don't reset to 1)
      },
    },
  },
  computed: {
    currentPage: {
      get() {
        // Use local state for immediate UI updates, fallback to prop
        return this.localPage;
      },
      set(value) {
        this.localPage = value;
        this.$emit('update:modelValue', value);
      },
    },
    totalPages() {
      return this.pageCount;
    },
    isMobile() {
      return this.windowWidth < 768;
    },
    isTablet() {
      return this.windowWidth >= 763 && this.windowWidth <= 1020;
    },
    visiblePages() {
      const pages = [];

      // On mobile, show specific pattern: 1-5 shows all, then 1 + ellipsis + 3 pages around current
      if (this.isMobile) {
        if (this.currentPage <= 5) {
          // Pages 1-5: show 1, 2, 3, 4, 5
          const maxPages = Math.min(5, this.totalPages);
          for (let i = 1; i <= maxPages; i++) {
            pages.push(i);
          }
        } else {
          // Page 6+: show 3 pages ending at current page (current-2, current-1, current)
          // Page 1 will be shown separately, ellipsis will be shown separately
          // Never show beyond the current page on mobile
          let start = this.currentPage - 2;
          let end = this.currentPage; // Always end at current page, never beyond

          // Ensure we don't include page 1 (it's shown separately)
          if (start < 2) {
            start = 2;
          }

          // Ensure end doesn't exceed total pages (safety check)
          if (end > this.totalPages) {
            end = this.totalPages;
            // Adjust start if needed, but keep at least 2
            start = Math.max(2, end - 2);
          }

          // Add pages from start to end (all will be > 1)
          for (let i = start; i <= end; i++) {
            pages.push(i);
          }
        }
      } else if (this.isTablet) {
        // Tablet (763px - 1020px): show fewer pages to prevent overflow
        // Show 5 pages around current (total 7 with first and last page)
        const pagesAroundCurrent = 5;
        const halfRange = Math.floor(pagesAroundCurrent / 2); // 2 pages on each side

        let start = this.currentPage - halfRange;
        let end = this.currentPage + halfRange;

        // Adjust if we're near the beginning
        if (start < 2) {
          start = 2;
          end = Math.min(start + pagesAroundCurrent - 1, this.totalPages - 1);
        }

        // Adjust if we're near the end
        if (end > this.totalPages - 1) {
          end = this.totalPages - 1;
          start = Math.max(2, end - pagesAroundCurrent + 1);
        }

        // Build the page range (excludes page 1 and last page, they're shown separately)
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
      } else {
        // Desktop: show exactly 9 pages around current (total 11 with first and last page)
        // Pattern: 1, ..., current-4, current-3, current-2, current-1, current, current+1, current+2, current+3, current+4, ..., last
        const pagesAroundCurrent = 9;
        const halfRange = Math.floor(pagesAroundCurrent / 2); // 4 pages on each side

        let start = this.currentPage - halfRange;
        let end = this.currentPage + halfRange;

        // Adjust if we're near the beginning - ensure we show 9 pages starting from as low as possible
        if (start < 2) {
          // If start would be < 2, start from 2 (page 1 is shown separately)
          start = 2;
          end = Math.min(start + pagesAroundCurrent - 1, this.totalPages - 1); // Don't include last page
        }

        // Adjust if we're near the end - ensure we show 9 pages ending as high as possible
        if (end > this.totalPages - 1) {
          // If end would be >= totalPages, end at totalPages - 1 (last page is shown separately)
          end = this.totalPages - 1;
          start = Math.max(2, end - pagesAroundCurrent + 1); // Don't include page 1
        }

        // Build the page range (excludes page 1 and last page, they're shown separately)
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
      }

      return pages;
    },
    shouldShowFirstPageOnMobile() {
      // On mobile, always show page 1 when currentPage > 5
      return this.isMobile && this.currentPage > 5;
    },
    shouldShowFirstEllipsisOnMobile() {
      // On mobile, show ellipsis after page 1 when currentPage > 5
      return this.isMobile && this.currentPage > 5;
    },
    shouldShowFirstPageOnDesktop() {
      // On desktop/tablet, show first page if visiblePages doesn't start at 1
      if (this.isMobile) return false;
      return this.visiblePages.length > 0 && this.visiblePages[0] > 1;
    },
    shouldShowFirstEllipsisOnDesktop() {
      // On desktop/tablet, show ellipsis if there's a gap between 1 and first visible page
      if (this.isMobile) return false;
      return this.visiblePages.length > 0 && this.visiblePages[0] > 2;
    },
    shouldShowLastEllipsisOnDesktop() {
      // On desktop/tablet, show ellipsis if there's a gap between last visible page and total pages
      if (this.isMobile) return false;
      const lastVisible = this.visiblePages[this.visiblePages.length - 1];
      return lastVisible < this.totalPages - 1;
    },
    shouldShowLastPageOnDesktop() {
      // On desktop/tablet, show last page if visiblePages doesn't end at totalPages
      if (this.isMobile) return false;
      const lastVisible = this.visiblePages[this.visiblePages.length - 1];
      return lastVisible < this.totalPages;
    },
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) {
        return;
      }

      // Set flag to prevent watcher from overwriting during async parent update
      this.isUserAction = true;

      // Update local state immediately for instant UI feedback
      this.currentPage = page;

      // Call the click handler if provided (parent may handle pagination here)
      if (this.clickHandler && typeof this.clickHandler === 'function') {
        this.clickHandler(page);
      }

      // Reset flag after parent has had time to update (or timeout if parent doesn't update)
      this.$nextTick(() => {
        setTimeout(() => {
          // Check if parent updated modelValue to match our click
          if (this.modelValue === page) {
            this.isUserAction = false;
          } else {
            // Parent hasn't updated yet, keep flag a bit longer
            setTimeout(() => {
              this.isUserAction = false;
            }, 200);
          }
        }, 100);
      });
    },
  },
};
</script>

<style scoped>
.pagination {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
}

.page-item {
  margin: 0 2px;
}

.page-link {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  text-decoration: none;
  border: 1px solid #dee2e6;
  color: #007bff;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.page-link:hover {
  color: #0056b3;
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.page-link.active {
  color: #fff;
  background-color: #007bff;
  border-color: #007bff;
}

.page-link:disabled,
.page-link.disabled {
  color: #6c757d;
  background-color: #fff;
  border-color: #dee2e6;
  cursor: not-allowed;
}

/* Ensure prev/next buttons are grey when disabled */
/* Target buttons with disabled attribute - using !important to override Tailwind */
button:disabled,
button[disabled],
button:disabled[class*="prev"],
button:disabled[class*="next"] {
  color: #6c757d !important;
  cursor: not-allowed !important;
  opacity: 0.65 !important;
  pointer-events: none !important;
}

/* Target by disabled class - must override Tailwind classes from parent */
button.disabled,
.disabled,
button[class*="prev"].disabled,
button[class*="next"].disabled {
  color: #6c757d !important;
  opacity: 0.65 !important;
}

.break-view-link {
  cursor: default;
}

.break-view-link:hover {
  background-color: #fff;
  border-color: #dee2e6;
  color: #007bff;
}

button.rounded-circle[disabled] {
  pointer-events: none;
  background-color: transparent !important;
}

.break-view-class {
  padding: 0 15px;
  width: 34px;
}
</style>

<style>
/* Non-scoped styles to ensure disabled button styling works */
/* These styles need to override Tailwind classes from parent */
.custom-pagination-wrapper button:disabled,
.custom-pagination-wrapper button[disabled] {
  color: #6c757d !important;
  opacity: 0.65 !important;
  cursor: not-allowed !important;
  pointer-events: none !important;
}

.custom-pagination-wrapper .disabled,
.custom-pagination-wrapper button.disabled {
  color: #6c757d !important;
  opacity: 0.65 !important;
}
</style>
